/* sp-main-product.js — gallery, sticky add-to-cart bar, ships-by date. No UI text.
   Bundle tiers, variant units and add-to-cart live in sp-bundle.js. */
(function () {
  'use strict';

  /* ---------- Gallery ---------- */
  class SpGallery extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-sp-gallery-list]');
      this.slides = Array.from(this.querySelectorAll('[data-sp-slide]'));
      this.dots = Array.from(this.querySelectorAll('.sp-gallery__dot'));
      this.thumbs = Array.from(this.querySelectorAll('[data-sp-thumb]'));
      this.thumbList = this.querySelector('[data-sp-thumb-list]');
      this.index = 0;
      const prev = this.querySelector('[data-sp-gallery-prev]'), next = this.querySelector('[data-sp-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach(d => d.addEventListener('click', () => this.goTo(+d.dataset.index)));
      this.thumbs.forEach(t => t.addEventListener('click', () => this.goTo(+t.dataset.index)));
      const tp = this.querySelector('[data-sp-thumbs-prev]'), tn = this.querySelector('[data-sp-thumbs-next]');
      if (tp) tp.addEventListener('click', () => this.scrollThumbs(-1));
      if (tn) tn.addEventListener('click', () => this.scrollThumbs(1));
      if (this.list) { let t; this.list.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => this.syncFromScroll(), 80); }, { passive: true }); }
      this.updateThumbArrows();
      if (this.thumbList) this.thumbList.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
      // `sp:gallery:goto` ({ detail: { mediaId } }) activates the slide carrying that data-media-id (variant
      // image swap). Accepted on the element itself and on the enclosing section root.
      this.onGoto = (e) => {
        if (e.currentTarget === this.sectionRoot && e.target === this) return; // already handled on the element
        const id = e && e.detail ? e.detail.mediaId : null;
        if (id != null) this.goToMedia(id);
      };
      this.addEventListener('sp:gallery:goto', this.onGoto);
      this.sectionRoot = this.closest('[data-section]');
      if (this.sectionRoot) this.sectionRoot.addEventListener('sp:gallery:goto', this.onGoto);
    }
    disconnectedCallback() {
      if (this.sectionRoot && this.onGoto) this.sectionRoot.removeEventListener('sp:gallery:goto', this.onGoto);
    }
    goToMedia(mediaId) {
      const i = this.slides.findIndex(s => String(s.dataset.mediaId) === String(mediaId));
      if (i >= 0) this.goTo(i);
    }
    goTo(i) {
      if (!this.slides.length || !this.list) return;
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      const slide = this.slides[i];
      this.list.scrollTo({ left: slide.offsetLeft - this.list.offsetLeft, behavior: 'smooth' });
      this.setActive(i);
    }
    syncFromScroll() {
      const x = this.list.scrollLeft; let best = 0, bd = Infinity;
      this.slides.forEach((s, i) => { const d = Math.abs((s.offsetLeft - this.list.offsetLeft) - x); if (d < bd) { bd = d; best = i; } });
      this.setActive(best);
    }
    setActive(i) {
      this.index = i;
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      this.thumbs.forEach((t, k) => { t.classList.toggle('is-active', k === i); if (k === i && this.thumbList) { const r = t.getBoundingClientRect(), lr = this.thumbList.getBoundingClientRect(); if (r.left < lr.left || r.right > lr.right) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); } });
      const prev = this.querySelector('[data-sp-gallery-prev]'), next = this.querySelector('[data-sp-gallery-next]');
      if (prev) prev.style.opacity = i === 0 ? '0.3' : '0.7';
      if (next) next.style.opacity = i === this.slides.length - 1 ? '0.3' : '0.7';
    }
    scrollThumbs(dir) { if (!this.thumbList) return; const w = this.thumbs[0] ? this.thumbs[0].getBoundingClientRect().width + 10 : 120; this.thumbList.scrollBy({ left: dir * w, behavior: 'smooth' }); }
    updateThumbArrows() {
      if (!this.thumbList) return;
      const tp = this.querySelector('[data-sp-thumbs-prev]'), tn = this.querySelector('[data-sp-thumbs-next]');
      const max = this.thumbList.scrollWidth - this.thumbList.clientWidth;
      if (tp) tp.disabled = this.thumbList.scrollLeft <= 1;
      if (tn) tn.disabled = this.thumbList.scrollLeft >= max - 1;
    }
  }
  if (!customElements.get('sp-gallery')) customElements.define('sp-gallery', SpGallery);

  /* ---------- Sticky add-to-cart bar ---------- */
  // Visibility + click pass-through only; the price/compare/badge inside the bar are written by sp-bundle.js.
  class SpStickyAtc extends HTMLElement {
    connectedCallback() {
      const sectionId = this.dataset.section;
      const root = document.getElementById('MainProduct-' + sectionId) || this.closest('[data-section]') || document;
      this.form = root.querySelector('#product-form-' + sectionId) || root.querySelector('form[data-type="add-to-cart-form"]');
      this.target = root.querySelector('[data-sp-atc]');
      const btn = this.querySelector('[data-sp-sticky-button]');
      if (btn) btn.addEventListener('click', () => {
        if (!this.form) return;
        if (this.form.requestSubmit) this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
      if (!this.target) return;
      // The bar appears once the main add-to-cart button's bottom edge leaves the viewport. No
      // IntersectionObserver: single-jump scrolls (scrollTo, anchor links, back navigation) never intersect
      // the element and produce no callback. A spacer at the end of the document keeps the fixed bar off the
      // footer.
      if (!document.querySelector('.sp-sticky-atc-spacer')) {
        const spacer = document.createElement('div');
        spacer.className = 'sp-sticky-atc-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        document.body.appendChild(spacer);
      }
      let pending = false;
      const measure = () => { pending = false; this.show(this.target.getBoundingClientRect().bottom < 0); };
      const schedule = () => { if (!pending) { pending = true; requestAnimationFrame(measure); } };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      measure();
    }
    show(on) {
      this.classList.toggle('is-visible', on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
  }
  if (!customElements.get('sp-sticky-atc')) customElements.define('sp-sticky-atc', SpStickyAtc);

  /* ---------- Ships-by date ---------- */
  function ordinal(n) { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); }
  function shipsBy() {
    document.querySelectorAll('[data-sp-ships-by]').forEach(el => {
      const days = parseInt(el.dataset.days || '1', 10), skip = el.dataset.skipWeekend === 'true';
      const months = (el.dataset.months || '').split(',');
      const d = new Date(); let n = 0;
      while (n < days) { d.setDate(d.getDate() + 1); if (skip && (d.getDay() === 0 || d.getDay() === 6)) continue; n++; }
      if (skip) while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
      const out = el.querySelector('[data-sp-ships-by-date]');
      // `day_month` (tr/de/fr/…): "30 Eylül" — no English ordinal suffix. `month_day` (en): "September 30th".
      const dayMonth = el.dataset.format === 'day_month';
      if (out && months.length === 12) out.textContent = dayMonth ? d.getDate() + ' ' + months[d.getMonth()] : months[d.getMonth()] + ' ' + ordinal(d.getDate());
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', shipsBy); else shipsBy();
  document.addEventListener('shopify:section:load', shipsBy);
})();
